import React, { useMemo } from 'react';
import { Input, Tooltip } from 'antd';
import { FiAlertTriangle } from 'react-icons/fi';
import { BIO_LIMITS, BioType } from '../NewCaseReverseUpload/constants';
import { calculateGA } from '../NewCaseReverseUpload/utils';


interface Props {
    type: BioType;
    value: string;
    onChange: (val: string) => void;
    disabled?: boolean;
    serverGA?: string | null;
    hasError?: boolean;
}

export const BiometryInput: React.FC<Props> = ({ type, value, onChange, disabled, serverGA, hasError }) => {
    const calc = useMemo(() => calculateGA(type, value), [type, value]);
    const limit = BIO_LIMITS[type];
    
    const isRangeError = value && !disabled && (parseFloat(value) < limit.min || parseFloat(value) > limit.max);
    
    const displayGA = serverGA ? serverGA : (calc && !calc.error ? calc.ga : null);
    
    const isErrorState = isRangeError || hasError;

    const bgClass = disabled 
        ? 'bg-slate-100 border-slate-200 dark:bg-gray-800 dark:border-gray-700 cursor-not-allowed' 
        : (isErrorState 
            ? 'border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-700' 
            : 'bg-white border-slate-200 dark:bg-gray-800 dark:border-gray-700 focus-within:border-blue-400 dark:focus-within:border-blue-500');
    
    const textClass = disabled ? 'text-slate-400 dark:text-gray-500' : 'text-slate-700 dark:text-gray-200';

    return (
        <Tooltip title={isRangeError ? `Valid: ${limit.min}-${limit.max} ${limit.unit}` : ''} color="red" trigger={['focus', 'hover']} open={!!isRangeError}>
            <div className={`rounded-md border px-3 py-2 flex items-center justify-between transition-all h-14 ${bgClass}`}>
                <div className="flex flex-col">
                    <span className="text-[10px] uppercase font-bold text-slate-400 dark:text-gray-500">{type} <span className="font-normal lowercase">({limit.unit})</span></span>
                    <Input 
                        variant="borderless" 
                        disabled={disabled}
                        className={`p-0 text-sm font-semibold !bg-transparent h-6 ${textClass} dark:placeholder-gray-600`} 
                        placeholder={disabled ? "Locked" : "0.0"}
                        value={value} 
                        onChange={e => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) onChange(v); }} 
                    />
                </div>
                {displayGA && (
                    <div className="bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-300 text-[11px] font-bold px-2 py-1 rounded border border-indigo-100 dark:border-indigo-800 whitespace-nowrap shadow-sm">
                        {displayGA}
                    </div>
                )}
                {isErrorState && !disabled && <FiAlertTriangle className="text-red-500" />}
            </div>
        </Tooltip>
    );
};